import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import globalStore from '@services/global.state';
import {goBack, http} from '@utils';
import {updatePassword} from './login.service';

const closeIcon = require('@components/assets/icons/close.webp');
const clearIcon = require('@components/assets/icons/clear.webp');
const lockIcon = require('@assets/icons/login/lock.webp');
const codeIcon = require('@components/assets/icons/code.webp');
const eyeCloseIcon = require('@assets/icons/login/eye-close.webp');
const eyeOpenIcon = require('@assets/icons/login/eye-open.webp');

const PASSWORD_REG = /^[A-Za-z0-9~!@#$%^&*()_+[\]{};:,.<>?]*$/;
const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,15}$/;

const IconButton = ({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex border-0 bg-transparent p-0 ${className || ''}`}>
    {children}
  </button>
);

const LoginIcon = ({src, className}: {src: string; className?: string}) => (
  <img src={src} alt="" className={className || 'h-4 w-4 shrink-0'} />
);

const NextUILoadingSpinner = () => (
  <div className="relative inline-flex flex-col items-center justify-center gap-2">
    <div className="relative flex h-5 w-5">
      <i className="absolute h-full w-full animate-spinner-ease-spin rounded-full border-2 border-solid border-b-current border-l-transparent border-r-transparent border-t-transparent" />
      <i className="absolute h-full w-full animate-spinner-linear-spin rounded-full border-2 border-dotted border-b-current border-l-transparent border-r-transparent border-t-transparent opacity-75" />
    </div>
  </div>
);

const NavTitle = ({title}: {title: string}) => (
  <div className="relative z-50 flex h-11 w-full flex-none items-center bg-white shadow-[0_1px_4px_0_rgba(0,0,0,0.25)]">
    <div className="flex flex-1 items-center justify-center font-bold text-t1">
      {title}
    </div>
    <button
      type="button"
      className="absolute right-0 top-0 flex h-full items-center border-0 bg-transparent px-3"
      onClick={goBack}>
      <LoginIcon src={closeIcon} className="h-6 w-6" />
    </button>
  </div>
);

const PasswordInput = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => {
  const [secure, setSecure] = useState(true);

  return (
    <div className="mt-4 flex items-center border-b border-[var(--bg-color-grey)] py-4">
      <div className="flex h-8 w-full items-center">
        <LoginIcon src={lockIcon} />
        <input
          className="ml-2 mr-4 flex-1 appearance-none border-0 bg-transparent p-0 text-base font-medium text-t1 shadow-none outline-0 placeholder:font-medium placeholder:text-t4 focus:border-0 focus:outline-0 focus:ring-0"
          type={secure ? 'password' : 'text'}
          placeholder={placeholder}
          value={value}
          onChange={event => {
            const nextValue = event.target.value;
            if (!nextValue || PASSWORD_REG.test(nextValue)) {
              onChange(nextValue);
            }
          }}
          maxLength={15}
        />
        {value ? (
          <IconButton className="mr-6" onClick={() => onChange('')}>
            <LoginIcon src={clearIcon} className="h-3 w-3" />
          </IconButton>
        ) : null}
        <IconButton className="mr-1" onClick={() => setSecure(!secure)}>
          <LoginIcon src={secure ? eyeCloseIcon : eyeOpenIcon} />
        </IconButton>
      </div>
    </div>
  );
};

const OTPInput = ({onChange}: {onChange: (value: string) => void}) => {
  const {i18n} = useTranslation();
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasOTP, setHasOTP] = useState(false);
  const [otpTime, setOtpTime] = useState(59);
  const otpTimeRef = useRef(59);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (hasOTP && otpTimeRef.current > 1) {
        otpTimeRef.current -= 1;
        setOtpTime(otpTimeRef.current);
      } else {
        window.clearInterval(timer);
        setHasOTP(false);
      }
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [hasOTP]);

  const updateValue = (nextValue: string) => {
    setValue(nextValue);
    onChange(nextValue);
  };

  return (
    <div className="mt-4 flex items-center border-b border-[var(--bg-color-grey)] py-4">
      <LoginIcon src={codeIcon} />
      <input
        className="ml-2 mr-4 w-28 flex-1 appearance-none border-0 bg-transparent p-0 text-base font-medium text-t1 shadow-none outline-0 placeholder:font-medium placeholder:text-t4 focus:border-0 focus:outline-0 focus:ring-0"
        type="text"
        placeholder={i18n.t('login.tip.otp')}
        value={value}
        onChange={event => {
          const nextValue = event.target.value;
          if (/^[0-9]+$/.test(nextValue) || nextValue === '') {
            updateValue(nextValue);
          }
        }}
        maxLength={6}
      />
      {value ? (
        <IconButton className="mr-6" onClick={() => updateValue('')}>
          <LoginIcon src={clearIcon} className="h-3 w-3" />
        </IconButton>
      ) : null}
      {hasOTP ? (
        <div className="flex h-8 w-12 items-center justify-center rounded bg-[#F2F6FF] p-3">
          <span className="text-xs text-t3">{otpTime}s</span>
        </div>
      ) : (
        <button
          type="button"
          disabled={loading}
          className="box-border inline-flex h-8 min-w-[4rem] items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded border-0 bg-[linear-gradient(180deg,#f2f6ff_0%,#dfe3ed_100%)] px-3 text-t2 shadow-[0_1px_0_0_#adb3c8] disabled:opacity-70"
          onClick={() => {
            if (loading) {
              return;
            }
            setLoading(true);
            http
              .post('app/sendCode', {
                userPhone: '',
              })
              .then(() => {
                globalStore.globalTotal.next({
                  type: 'success',
                  message: 'Successfully',
                });
                otpTimeRef.current = 59;
                setOtpTime(otpTimeRef.current);
                setHasOTP(true);
              })
              .catch(() => {})
              .finally(() => setLoading(false));
          }}>
          {loading ? <NextUILoadingSpinner /> : null}
          <span className="text-xs font-bold">
            {i18n.t('login.label.get-otp')}
          </span>
        </button>
      )}
    </div>
  );
};

const SetPassword = () => {
  const {i18n} = useTranslation();
  const [userPassword, setUserPassword] = useState('');
  const [userPasswordRet, setUserPasswordRet] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const showWarning = (message: string) => {
    globalStore.globalTotal.next({type: 'warning', message});
  };

  const onSubmit = () => {
    if (!PASSWORD_RULE.test(userPassword)) {
      showWarning(i18n.t('login.tip.passwordRule'));
      return;
    }
    if (userPassword !== userPasswordRet) {
      showWarning(i18n.t('login.tip.pwd-dif'));
      return;
    }
    if (!code) {
      showWarning(i18n.t('login.tip.no-otp'));
      return;
    }
    if (submitting) {
      return;
    }
    setSubmitting(true);
    updatePassword(userPassword, code)
      .then(() => {
        globalStore.globalTotal.next({
          type: 'success',
          message: i18n.t('login.tip.modified'),
        });
        goBack();
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="h-full bg-white">
      <NavTitle title={i18n.t('login.label.sign-title', 'Set Password')} />
      <div className="h-full w-full border-t border-[var(--bg-color-grey)] p-6">
        <PasswordInput
          value={userPassword}
          onChange={setUserPassword}
          placeholder="Enter Password"
        />
        <PasswordInput
          value={userPasswordRet}
          onChange={setUserPasswordRet}
          placeholder="Confirm Password"
        />
        <OTPInput onChange={setCode} />
        <button
          type="button"
          className={`mt-5 flex h-12 w-full items-center justify-center rounded-full border-0 bg-primary ${
            (!userPassword || !userPasswordRet || submitting) && 'opacity-50'
          }`}
          onClick={onSubmit}>
          <span className="text-base font-bold text-white">
            {i18n.t('login.tip.confirm-password')}
          </span>
        </button>
      </div>
    </div>
  );
};

export default SetPassword;
